/**
 * Narlock Systems Stage Pack
 *
 * Creates NS logo meshes with physics and theme integration.
 * Loads logo model from .glb file at runtime.
 * Supports multiple GLB variants via glbKey config.
 */

import { CubeTexture, Mesh, Vector3 } from "@babylonjs/core";
import {
  EventBus,
  NsManager,
  type StageConfigManager,
  type StagePack,
  type StageThemeInfo,
} from "@tony/nl-babylon";
import type { Theme } from "@/context/ThemeContext";

/**
 * Default NS configuration
 */
const DEFAULT_NS_CONFIG = {
  count: 1,
  scale: 3,
};

/**
 * GLB-specific configuration for NS logo variants
 */
interface NsGlbConfig {
  /** GLB filename (optional - uses default if not specified) */
  glbFile?: string;
  /** GLB path (optional - uses default if not specified) */
  glbPath?: string;
  /** Physics box extents (half-sizes) */
  extents: { x: number; y: number; z: number };
  /** Physics box center offset */
  center: { x: number; y: number; z: number };
  /** Model Y offset for positioning (bottom of model in local coords) */
  modelYOffset: number;
}

/**
 * GLB configurations for each NS logo variant
 * Keyed by glbKey from route query param
 */
export const NS_GLB_CONFIGS: Record<string, NsGlbConfig> = {
  "crystal-beta": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Crystal-Beta.glb",
    modelYOffset: 0,
  },
  "crystal-debug": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Crystal-Debug.glb",
    modelYOffset: 0,
  },
  "crystal-final": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Crystal-Final.glb",
    modelYOffset: 0,
  },
  "crystal-final-compressed": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Crystal-Final-Compressed.glb",
    modelYOffset: 0,
  },
  "crystal-v0-7": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Crystal-v0-7.glb",
    modelYOffset: 0,
  },
  "crystal-v0-7-compressed": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Crystal-v0-7-Compressed.glb",
    modelYOffset: 0,
  },
  "flat-beta": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Flat-Beta.glb",
    modelYOffset: 0,
  },
  "flat-final": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Flat-Final.glb",
    modelYOffset: 0,
  },
  "flat-final-compressed": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Flat-Final-Compressed.glb",
    modelYOffset: 0,
  },
  "spherical-beta": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Spherical-Beta.glb",
    modelYOffset: 0,
  },
  "spherical-final": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Spherical-Final.glb",
    modelYOffset: 0,
  },
  "spherical-final-compressed": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Spherical-Final-Compressed.glb",
    modelYOffset: 0,
  },
  "spherical-v0-7": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Spherical-v0-7.glb",
    modelYOffset: 0,
  },
  "spherical-v0-7-compressed": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Spherical-v0-7-Compressed.glb",
    modelYOffset: 0,
  },
  "spheroid-v0-8": {
    center: { x: 0, y: 0, z: 0 },
    extents: { x: 0.5, y: 0.5, z: 0.5 },
    glbFile: "ns-Spheroid-v0-8.glb",
    modelYOffset: 0,
  },
};

/** Valid GLB keys for NS logo variants */
export type NsGlbKey = keyof typeof NS_GLB_CONFIGS;

/** Default GLB key when none specified */
export const DEFAULT_NS_GLB_KEY: NsGlbKey = "flat-final";

/**
 * Narlock Systems stage pack definition
 */
export const nsStagePack: StagePack = {
  applyTheme: (meshes, theme: StageThemeInfo) => {
    // Get the stored nsManager
    const nsManager = (meshes as unknown as { __nsManager?: NsManager })
      .__nsManager;
    if (!nsManager) {
      // Fallback: create a temporary manager for theme application
      const eventBus = new EventBus();
      const tempManager = new NsManager(eventBus);
      tempManager.applyThemeColors(
        meshes,
        theme.themeId as Theme,
        theme.isDarkMode,
      );
      return;
    }

    // Collect all NS meshes including children for theme application
    const allMeshes: Mesh[] = [];
    for (const rootMesh of meshes) {
      allMeshes.push(rootMesh);
      for (const child of rootMesh.getChildMeshes()) {
        if (child instanceof Mesh) {
          allMeshes.push(child);
        }
      }
    }
    nsManager.applyThemeColors(
      allMeshes,
      theme.themeId as Theme,
      theme.isDarkMode,
    );
  },

  createAssets: async ({ physicsManager, scene }) => {
    // Read config from StageConfigManager (set by StageContext) or use defaults
    const stageConfigManager = (
      scene.metadata as { stageConfigManager?: StageConfigManager } | undefined
    )?.stageConfigManager;
    const stageConfig = stageConfigManager?.config;
    const config = {
      count: stageConfig?.count ?? DEFAULT_NS_CONFIG.count,
      scale: stageConfig?.scale ?? DEFAULT_NS_CONFIG.scale,
    };

    // Get GLB-specific config based on glbKey from route query param
    const glbKey = stageConfig?.glbKey ?? DEFAULT_NS_GLB_KEY;
    const glbConfig =
      NS_GLB_CONFIGS[glbKey] ?? NS_GLB_CONFIGS[DEFAULT_NS_GLB_KEY];

    // Ensure scene has an environment texture for PBR reflections
    if (!scene.environmentTexture) {
      const envTexture = CubeTexture.CreateFromPrefilteredData(
        "/3d/env/sanGiuseppeBridge.env",
        scene,
      );
      envTexture.rotationY = 5.54;
      scene.environmentTexture = envTexture;
    }

    console.log(
      "[NsStagePack] Creating with config:",
      config,
      "glbKey:",
      glbKey,
    );

    const eventBus = new EventBus();
    const nsManager = new NsManager(eventBus);
    await nsManager.initialize();
    nsManager.setScene(scene);

    const meshes: Mesh[] = [];

    for (let i = 0; i < config.count; i++) {
      // Create NS logo using NsManager (async - loads from .glb file)
      // Use GLB config to specify custom file/path if needed
      const nsResult = await nsManager.createNs(scene, {
        glbFile: glbConfig.glbFile,
        glbPath: glbConfig.glbPath,
        scale: config.scale,
      });

      const { rootMesh } = nsResult;

      // Position: center for single logo, circle for multiple
      // Use glbConfig.modelYOffset to compensate for model's bottom position
      const modelYOffset = glbConfig.modelYOffset * config.scale;
      const startY = 4 - modelYOffset + Math.random() * 0.5; // Start ~4 units up, will fall
      let startX = 0;
      let startZ = 0;

      if (config.count > 1) {
        const angle = (i / config.count) * Math.PI * 2;
        const radius = 3 + Math.random() * 2;
        startX = Math.cos(angle) * radius;
        startZ = Math.sin(angle) * radius;
      }

      rootMesh.position.set(startX, startY, startZ);
      rootMesh.rotation.y = Math.random() * Math.PI * 2;

      // Mark root as physics root for InteractionManager
      rootMesh.metadata = { ...rootMesh.metadata, isPhysicsRoot: true };

      // Add physics body - BOX with explicit extents from glbConfig
      const nsExtents = {
        x: glbConfig.extents.x * config.scale,
        y: glbConfig.extents.y * config.scale,
        z: glbConfig.extents.z * config.scale,
      };
      const nsCenter = {
        x: glbConfig.center.x * config.scale,
        y: glbConfig.center.y * config.scale,
        z: glbConfig.center.z * config.scale,
      };
      physicsManager.addPhysicsBody(rootMesh, {
        angularDamping: 0.8,
        center: nsCenter,
        extents: nsExtents,
        friction: 0.7,
        linearDamping: 0.5,
        mass: 3.0,
        restitution: 0.15,
        shapeType: "BOX",
      });

      meshes.push(rootMesh);
    }

    // Store nsManager reference for theme updates
    (meshes as unknown as { __nsManager: NsManager }).__nsManager = nsManager;

    return meshes;
  },
  displayName: "Narlock Systems",
  id: "ns",

  interactionConfig: {
    hoverEffects: {
      edgeRenderer: false,
      outlineEdges: true,
    },
    selectionEffects: true,
  },

  physicsConfig: {
    bodyOptions: {
      angularDamping: 0.8,
      friction: 0.7,
      linearDamping: 0.5,
      mass: 3.0,
      restitution: 0.15,
      shapeType: "BOX",
    },
    gravity: new Vector3(0, -9.8, 0),
  },
};
